import React, { ComponentProps, ReactNode, Ref, useMemo } from 'react';

import { OffsetOptions } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { noop, Container } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import Portal from '@adsk/alloy-react-portal';
import { type StyleProp } from '@adsk/alloy-react-theme';

import { TRIGGER_EVENTS, TRIGGER_TARGETS } from './consts';
import { Variant } from './types';
import useTooltip, { UseTooltipProps } from './useTooltip';

const getOffsetSize = (offset: OffsetOptions): number | undefined => {
  if (typeof offset === 'number') {
    return offset;
  }

  return (offset as { mainAxis?: number }).mainAxis;
};

const defaultRenderTarget = (props: ComponentProps<typeof Overlay.Target>) => (
  <Overlay.Target {...props} />
);
const defaultRenderArrow = (props: ComponentProps<typeof Overlay.Arrow>) => (
  <Overlay.Arrow {...props} />
);
const defaultRenderBody = (props: ComponentProps<typeof Overlay.Body>) => (
  <Overlay.Body {...props} />
);

export type TooltipProps = Pick<
  UseTooltipProps,
  | 'delayHide'
  | 'delayShow'
  | 'flipArgs'
  | 'hideArgs'
  | 'offset'
  | 'onHide'
  | 'onShow'
  | 'overlayRef'
  | 'placement'
  | 'shiftArgs'
  | 'show'
  | 'trigger'
  | 'triggerTarget'
> & {
  /* The container to render the tooltip in */
  container?: Container;
  /* The contents to show within the popover. Hidden when no content available */
  content: ReactNode;
  /* The content of the element to which the tooltip is applied */
  children?: ReactNode;
  /* className to add to the tooltip overlay */
  className?: string;
  /* style to tooltip overlay */
  style?: StyleProp;
  /* Tooltip variant */
  variant?: Variant;
  /* Whether the tooltip should close when clicked outside when is has a click trigger */
  rootClose?: boolean;
  /* Whether to not have the arrow - show and default to true */
  showArrow?: boolean;
  /*
   * Prop getter to apply props to the trigger/target element.
   * Use this instead of applying props directly
   */
  getTargetProps?: () => Record<string, unknown>;
  'data-testid'?: string;
  /* target element ref (get/set).  Prefer to use the ref returned from `getReferenceProps`  */
  target?: Ref<HTMLElement>;
  /* Render function to modify the target */
  renderTarget?: (props: ComponentProps<typeof Overlay.Target>) => ReactNode;
  /* Render function to modify the arrow */
  renderArrow?: (props: ComponentProps<typeof Overlay.Arrow>) => ReactNode;
  /* Render function to modify the body (the element surrounding the content) */
  renderBody?: (props: ComponentProps<typeof Overlay.Body>) => ReactNode;
  overlayStyle?: StyleProp;
};

const Tooltip = ({
  children,
  className,
  style,
  container = document.body,
  content,
  variant = Overlay.VARIANTS.DARK,
  showArrow = true,
  offset = { crossAxis: 0, mainAxis: 10 },
  rootClose = true,
  placement: placementProp = Overlay.PLACEMENTS.TOP,
  show: showProp,
  target,
  overlayRef,
  getTargetProps = () => ({}),
  onHide: onHideProp = noop,
  onShow: onShowProp = noop,
  trigger = TRIGGER_EVENTS.HOVER,
  triggerTarget = TRIGGER_TARGETS.CHILDREN,
  delayShow = 300,
  delayHide = 0,
  'data-testid': dataTestid,
  renderTarget = defaultRenderTarget,
  renderArrow = defaultRenderArrow,
  renderBody = defaultRenderBody,
  overlayStyle,
  ...props
}: TooltipProps) => {
  const {
    show,
    placement,
    interactive,
    getReferenceProps,
    getFloatingProps,
    getArrowProps,
  } = useTooltip({
    enableCloseOnClickOutside: rootClose,
    placement: placementProp,
    show: showProp,
    trigger,
    triggerTarget,
    delayShow,
    delayHide,
    onHide: onHideProp,
    onShow: onShowProp,
    targetRef: target,
    overlayRef,
    offset,
    variant,
    ...props,
  });

  const offsetSize = getOffsetSize(offset);

  const floatingProps = useMemo(() => getFloatingProps(), [getFloatingProps]);

  return (
    <>
      {renderTarget({
        wrapper: 'div',
        shouldWrapChildren: true,
        ...getReferenceProps(getTargetProps()),
        children,
      })}

      {show && Boolean(content) && (
        <Portal container={container}>
          <Overlay.Container
            placement={placement}
            data-testid={dataTestid}
            {...floatingProps}
            style={
              floatingProps.style
                ? [floatingProps.style as StyleProp, overlayStyle]
                : overlayStyle
            }
          >
            {showArrow && renderArrow(getArrowProps())}
            {renderBody({
              className,
              style,
              variant,
              children: content,
            })}
            {interactive && (
              <Overlay.Blank placement={placement} size={offsetSize} />
            )}
          </Overlay.Container>
        </Portal>
      )}
    </>
  );
};

Tooltip.displayName = 'Tooltip';

Tooltip.propTypes = {
  /*
   * className to add to the tooltip overlay
   */
  className: PropTypes.string,
  /*
   * style to tooltip overlay
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /*
   * The contents to show within the popover.
   * hidden when no content available
   */
  content: PropTypes.node,
  /*
   * The content of the element to which the tooltip is applied
   */
  children: PropTypes.node,
  /*
   * Tooltip variant
   */
  variant: PropTypes.oneOf(Object.values(Overlay.VARIANTS)),
  /*
   * Whether the showing is triggered by click or mouseover
   */
  trigger: PropTypes.oneOf(Object.values(TRIGGER_EVENTS)),
  /*
   * The Overlay target (either the children or the tooltip itself)
   */
  triggerTarget: PropTypes.oneOf(Object.values(TRIGGER_TARGETS)),
  /*
   * Prop getter to apply props to the trigger/target element.
   * Use this instead of applying props directly
   */
  getTargetProps: PropTypes.func,
  /*
   * Overlay position according to Overlay placements.
   */
  placement: PropTypes.oneOf(Object.values(Overlay.PLACEMENTS)),
  /*
   * Target element ref (get/set).
   */
  target: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /*
   * Overlay ref to overlay dom element.
   */
  overlayRef: PropTypes.any,
  /*
   * Specify whether the overlay should trigger `onHide` when the user clicks outside the overlay.
   */
  rootClose: PropTypes.bool,
  /*
   * Whether the popover is showed or not. Should be sent only for
   * controlled elements
   */
  show: PropTypes.bool,
  /*
   * Callback to be called on show
   */
  onShow: PropTypes.func,
  /*
   * Callback to be called on hide
   */
  onHide: PropTypes.func,
  /*
   * Delay before show in milliseconds
   */
  delayShow: PropTypes.number,
  /*
   * Delay before hide in milliseconds
   */
  delayHide: PropTypes.number,
  /*
   * Whether to not have the arrow - show and default to true
   */
  showArrow: PropTypes.bool,
  /* Options that modify how the tooltip overlay can shift (see floating-ui documentation https://floating-ui.com/docs/shift) */
  shiftArgs: PropTypes.any,
  /* Options that modify how the tooltip overlay can flip (see floating-ui documentation https://floating-ui.com/docs/flip) */
  flipArgs: PropTypes.any,
  /* Options that modify how the tooltip overlay can hide (when target is hidden) (see floating-ui documentation https://floating-ui.com/docs/hide) */
  hideArgs: PropTypes.any,
};

export default Object.assign(Tooltip, {
  Arrow: Overlay.Arrow,
  Target: Overlay.Target,
  Body: Overlay.Body,
  VARIANTS: Overlay.VARIANTS,
  PLACEMENTS: Overlay.PLACEMENTS,
  TRIGGER_EVENTS,
  TRIGGER_TARGETS,
});
